// index.tsx (updated)
import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import webfonts from './webfonts.json';
import { FontSelector } from '../../../../components/generic/fontSelector';
import { FilePondUploader } from '../../../../components/generic/filePondUploader';
import { useDispatch, useSelector } from 'react-redux';
import { getLocationDetails } from '../../../../redux/slices/location/locationDetails';
import { useParams } from 'react-router-dom';
import BrandColorPicker from '../../../../components/generic/brandColorsPicker';
import {
  fetchBrandingSettings,
  resetBranding,
  saveBrandingSettings,
  updateBrandingData
} from '../../../../redux/slices/branding';
import toast from 'react-hot-toast';
import { ConfirmationModal } from '../../../../components/lomavisCreator/components/confirmationModal';

export function FieldTitle({ children }: { children: React.ReactNode }) {
  return (
    <span className="fs-4 fw-bold text-dark user-select-none me-1">
      {children}
    </span>
  );
}

export function FieldHelperText({ children }: { children: React.ReactNode }) {
  return <div className="text-muted mb-2">{children}</div>;
}

export default function SettingsBrandingTemplateBranding() {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const { locationslug } = useParams();
  const locationDetails = useSelector((state: any) => state.locationDetails);
  const {
    data: {
      selectedFont,
      primaryColor,
      secondaryColor,
      squareLogo,
      rectangleLogo
    }
  } = useSelector((state: any) => state.branding);
  const [showConfirm, setShowConfirm] = React.useState(false);
  useEffect(() => {
    dispatch(getLocationDetails(locationslug) as any);
  }, [dispatch, locationslug]);

  useEffect(() => {
    if (locationDetails?.uuid) {
      dispatch(fetchBrandingSettings(locationDetails.uuid) as any);
    }
  }, [dispatch, locationDetails]);

  const handlePrimaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateBrandingData({ primaryColor: e.target.value }));
  };
  const handleSecondaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateBrandingData({ secondaryColor: e.target.value }));
  };
  const handleSquareFilesUpdate = useCallback(
    (file) => dispatch(updateBrandingData({ squareLogo: file })),
    [dispatch]
  );
  const handleRectangleFilesUpdate = useCallback(
    (file) => dispatch(updateBrandingData({ rectangleLogo: file })),
    [dispatch]
  );
  const squareFiles = useMemo(
    () => (squareLogo ? [squareLogo] : []),
    [squareLogo]
  );
  const rectangleFiles = useMemo(
    () => (rectangleLogo ? [rectangleLogo] : []),
    [rectangleLogo]
  );

  const handleSave = () => {
    const data = {
      primary: primaryColor,
      secondary: secondaryColor,
      fontFamily: selectedFont?.family,
      squareLogo: squareLogo?.uploadPreset?.uuid,
      rectangleLogo: rectangleLogo?.uploadPreset?.uuid,
      uuid: locationDetails?.uuid
    };
    dispatch(
      saveBrandingSettings(
        data,
        () =>
          toast.success(
            t('lomavis.settings.branding.template_branding.save_success'),
            { position: 'top-right' }
          ),
        () =>
          toast.error(
            t('lomavis.settings.branding.template_branding.save_error'),
            { position: 'top-right' }
          )
      ) as any
    );
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        <div className="page-heading d-flex text-dark fw-bold fs-3 mb-10">
          {t('lomavis.settings.branding.template_branding.title')}
        </div>

        <div className="mb-5">
          <div className="rounded bg-light-secondary px-5 py-5 text-center mb-5">
            {t('lomavis.settings.branding.template_branding.description')}
          </div>

          <FieldTitle>{t('lomavis.branding.logo_square')}</FieldTitle>
          <FieldHelperText>
            {t('lomavis.branding.logo_square_helper')}
          </FieldHelperText>
          <FilePondUploader
            defaultFiles={squareFiles}
            onFilesUpdate={handleSquareFilesUpdate}
            allowMultiple={false}
            shape="square"
            name={t('lomavis.settings.branding.filepond_name_file')}
          />

          <div className="mt-4" />

          <FieldTitle>{t('lomavis.branding.logo_rectangle')}</FieldTitle>
          <FieldHelperText>
            {t('lomavis.branding.logo_rectangle_helper')}
          </FieldHelperText>
          <FilePondUploader
            name={t('lomavis.settings.branding.filepond_name_file')}
            defaultFiles={rectangleFiles}
            onFilesUpdate={handleRectangleFilesUpdate}
            allowMultiple={false}
            shape="rectangle"
          />

          <div className="mt-4">
            <FieldTitle>
              {t('lomavis.settings.branding.template_branding.font_settings')}
            </FieldTitle>
            <FieldHelperText>
              {t(
                'lomavis.settings.branding.template_branding.font_settings_helper'
              )}
            </FieldHelperText>
            <FontSelector
              selectedFont={selectedFont}
              onFontChange={(newFont) =>
                dispatch(updateBrandingData({ selectedFont: newFont }))
              }
              options={webfonts.items}
            />
          </div>

          <BrandColorPicker
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            handlePrimaryChange={handlePrimaryChange}
            handleSecondaryChange={handleSecondaryChange}
          />
        </div>
      </div>

      <div className="card-footer d-flex justify-content-end my-10">
        <button
          className="btn btn-danger me-2 btn-sm"
          onClick={() => {
            setShowConfirm(true);
          }}
        >
          {t('lomavis.settings.branding.template_branding.reset_button')}
        </button>
        <button className="btn btn-primary btn-sm" onClick={handleSave}>
          {t('lomavis.settings.branding.template_branding.save_button')}
        </button>
      </div>
      <ConfirmationModal
        title={t('lomavisBranding.resetModal.title')}
        body={t('lomavisBranding.resetModal.body')}
        primaryText={t('lomavisBranding.resetModal.primaryText')}
        secondaryText={t('lomavisBranding.resetModal.secondaryText')}
        show={showConfirm}
        handleClose={() => {
          setShowConfirm(false);
        }}
        handlePrimary={() => {
          dispatch(resetBranding());
          const data = {
            primary: null,
            secondary: null,
            fontFamily: null,
            squareLogo: null,
            rectangleLogo: null,
            uuid: locationDetails?.uuid
          };

          dispatch(
            saveBrandingSettings(
              data,
              () =>
                toast.success(
                  t(
                    'lomavis.settings.branding.template_branding.reset_success'
                  ),
                  { position: 'top-right' }
                ),
              () =>
                toast.error(
                  t('lomavis.settings.branding.template_branding.reset_error'),
                  { position: 'top-right' }
                ),
              true
            ) as any
          );
          setShowConfirm(false);
        }}
        handleSecondary={() => {
          setShowConfirm(false);
        }}
      />
    </div>
  );
}
